<script setup>
import moment from 'moment'
import debounce from 'debounce'
import { ref, watch, reactive } from 'vue'
import api from '@helpers/api/client'
import {$cart} from '../store'
import Spinner from '@components/Spinner.vue'

const props = defineProps(['listing'])

const today = moment().format('YYYY-MM-DD')
const designs = ['1', '2', '3']
const denominations = [150, 100, 75, 50]
const method = ref('self')
const amount = ref(100)
const customAmount = ref(null)
const addingToCart = ref(false)
const recipientEmailMismatch = ref(false)
const confirmRecipientEmail = ref('')
const form = reactive({
  design: designs[0],
  sender_name: '',
  recipient_name: '',
  recipient_email: '',
  send_on: today,
  message: '',
})

function updateRecipientEmail() {
  recipientEmailMismatch.value = form.recipient_email !== confirmRecipientEmail.value
      && form.recipient_email
      && confirmRecipientEmail.value
}

const updateCustom = debounce(function () {
  amount.value = customAmount.value = Math.min(Math.max(Math.round(+customAmount.value || 0), 10), 500)
}, 750)

function clickDenomination(denomination) {
  amount.value = +denomination
  customAmount.value = null
}

async function addToCart() {
  if (recipientEmailMismatch.value) {
    return false
  }

  const product = props.listing.products.find(({ price }) => Number(price) === amount.value)

  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: 'add_to_cart',
    cart_action: 'Single',
    ecommerce: {
      currency: 'USD',
      items: [{
        item_id: props.listing.id.toString(),
        item_name: props.listing.title,
        item_brand: props.listing?.product_line?.brand?.title || '',
        item_list_id: 'PDP',
        item_list_name: 'PDP',
        price: Number(product?.price || 0).toString(),
        quantity: '1',
      }]
    },
  })

  addingToCart.value = true
  const newCart = await api.post(`/carts/${$cart.get().id}/add`, {
    product_id: product?.id,
    listing_plan_id: null,
    quantity: 1,
    properties: form,
  }).then(({ data }) => data.data)
  $cart.set(newCart)
  addingToCart.value = false
}

watch(() => form.recipient_email, debounce(function () {
  updateRecipientEmail()
}, 750));

watch(confirmRecipientEmail, debounce(function () {
  updateRecipientEmail()
}, 750));
</script>

<template>
  <form @submit.prevent="addToCart" class="gift-card">
    <div class="gift-card__desktop gift-card__designs">
      <div class="gift-card__selected-design">
        <img :src="`/images/gc-design-${form.design}.png`" :alt="form.design" />
      </div>
      <div class="gift-card__thumbnails">
        <div
            class="gift-card__thumbnail"
            :class="form.design === design ? 'gift-card__thumbnail--selected' : ''"
            v-for="design in designs"
            :key="design"
            @click="form.design = design"
        >
          <img :src="`/images/gc-design-${design}.png`" :alt="design" />
        </div>
      </div>
    </div>

    <div class="gift-card__main">
      <div class="gift-card__subtitle">
        Hometown Hero Gift Card
      </div>
      <div class="gift-card__title">
        Give the gift they <span>really</span> want
      </div>

      <div class="gift-card__mobile">
        <div class="gift-card__label">
          First, pick your design
        </div>

        <div class="gift-card__designs">
          <div class="gift-card__selected-design">
            <img :src="`/images/gc-design-${form.design}.png`" :alt="form.design" />
          </div>
          <div class="gift-card__thumbnails">
            <div
                class="gift-card__thumbnail"
                :class="form.design === design ? 'gift-card__thumbnail--selected' : ''"
                v-for="design in designs"
                :key="design"
                @click="form.design = design"
            >
              <img :src="`/images/gc-design-${design}.png`" :alt="design" />
            </div>
          </div>
        </div>
      </div>

      <div class="gift-card__section">
        <div class="gift-card__label">
          Choose amount

          <div class="gift-card__popular" v-if="amount === 100">
            Most popular
          </div>
        </div>
        <div class="gift-card__denominations">
          <button
              type="button"
              class="gift-card__denomination gift-card__button"
              :class="amount === denomination ? 'gift-card__button--active' : ''"
              @click="clickDenomination(denomination)"
              v-for="denomination in denominations"
              :key="denomination"
          >
            ${{ denomination }}
          </button>
        </div>
        <div class="gift-card__label gift-card__label--custom">
          Or enter custom amount
        </div>
        <input type="number" class="gift-card__input" placeholder="Enter custom amount" v-model="customAmount" @input="updateCustom" />
      </div>
      <div class="gift-card__section">
        <div class="gift-card__label">
          How would you like to send it?
        </div>
        <div class="gift-card__methods">
          <button
              type="button"
              class="gift-card__method gift-card__button"
              :class="method === 'self' ? 'gift-card__button--active' : ''"
              @click="method = 'self'"
          >
            Email to me
          </button>
          <button
              type="button"
              class="gift-card__method gift-card__button"
              :class="method === 'other' ? 'gift-card__button--active' : ''"
              @click="method = 'other'"
          >
            Email to them
          </button>
        </div>
      </div>
      <div v-if="method === 'self'">
        <div class="gift-card__section">
          <div class="gift-card__label">
            Recipient name
          </div>
          <input type="text" class="gift-card__input" placeholder="Recipient name" v-model="form.recipient_name" required />
        </div>
      </div>
      <div v-if="method === 'other'">
        <div class="gift-card__section">
          <div class="gift-card__form-section">
            <div class="gift-card__label">
              Who's it for?
            </div>
            <input type="text" class="gift-card__input" placeholder="Recipient name" v-model="form.recipient_name" required />
            <input type="email" class="gift-card__input" placeholder="Recipient email" v-model="form.recipient_email" required />
            <input type="email" class="gift-card__input" placeholder="Confirm recipient email" v-model="confirmRecipientEmail" required />

            <div class="gift-card__error" v-if="recipientEmailMismatch">
              Error! Emails must match!
            </div>
          </div>

          <div class="gift-card__form-section">
            <div class="gift-card__label">
              Who's it from?
            </div>
            <input type="text" class="gift-card__input" placeholder="Sender name" v-model="form.sender_name" required />
          </div>
        </div>
        <div class="gift-card__section">
          <div class="gift-card__label">
            Schedule delivery date
          </div>
          <input type="date" :min="today" class="gift-card__input" placeholder="Choose a date" v-model="form.send_on" required />
        </div>
        <div class="gift-card__section">
          <div class="gift-card__label">
            Want to add a personalized message?
          </div>
          <input type="text" class="gift-card__input" placeholder="Your message" v-model="form.message" />
        </div>
      </div>

      <button type="submit" class="gift-card__add-to-cart" :disabled="addingToCart">
        <Spinner v-if="addingToCart" />
        <span v-else>Add To Cart</span>
      </button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.gift-card {
  margin: 32px;

  @media screen and (min-width: 768px) {
    display: flex;
    margin: 100px auto;
    max-width: 1080px;
    gap: 90px;
  }

  .gift-card__desktop {
    display: none;
  }
  @media screen and (min-width: 768px) {
    .gift-card__desktop {
      display: block;
    }
    .gift-card__mobile {
      display: none;
    }
  }

  .gift-card__designs {
    @media screen and (min-width: 768px) {
      flex: 40%;
    }

    .gift-card__selected-design {
      img {
        display: block;
        width: 100%;
      }
    }

    .gift-card__thumbnails {
      display: flex;
      margin: 20px 0 32px 0;
      gap: 13px;

      @media screen and (min-width: 768px) {
        margin: 36px 0;
      }

      .gift-card__thumbnail {
        flex: 1;
        border-radius: 5px;
        background: #FFF;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.10);
        border: 2px solid transparent;
        padding: 8px;

        @media screen and (min-width: 768px) {
          border-radius: 8px;
          box-shadow: 0 3px 18px 0 rgba(0, 0, 0, 0.10);
          padding: 12px;
        }

        &.gift-card__thumbnail--selected {
          border-color: var(--Neutral-900, #4F4C47);
        }

        img {
          display: block;
          width: 100%;
        }
      }
    }
  }

  .gift-card__main {
    @media screen and (min-width: 768px) {
      flex: 60%;
    }

    .gift-card__subtitle {
      font-size: 18px;
      font-weight: 600;
      line-height: 41px; /* 227.778% */
      margin-bottom: 4px;
    }

    .gift-card__title {
      font-size: 42px;
      font-weight: 800;
      line-height: 45px; /* 107.143% */
      margin-bottom: 32px;

      @media screen and (min-width: 768px) {
        font-size: 50px;
        line-height: 53.5px; /* 107% */
      }

      span {
        color: var(--primary-200, #FF9F1C);
        font-style: italic;
      }
    }

    .gift-card__section {
      margin-bottom: 32px;
      padding-bottom: 32px;
      border-bottom: 2px solid var(--neutral-200, #D8D7D6);
    }

    .gift-card__label {
      font-size: 18px;
      font-weight: 600;
      line-height: 41px; /* 227.778% */
      margin-bottom: 12px;

      &.gift-card__label--custom {
        margin: 16px 0;
      }
    }

    .gift-card__error {
      color: var(--secondary-600, #9F5049);
      border-radius: 2px;
      border: 3px solid var(--secondary-600, #9F5049);
      background: #FCEBEA;
      padding: 14px 40px;
      margin-top: 12px;
    }

    .gift-card__popular {
      display: inline-block;
      border-radius: 5px;
      background: var(--primary-300, #E37F31);
      color: var(--shades-white, #FFF);
      text-align: center;
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px; /* 125% */
      text-transform: capitalize;
      padding: 4px 20px;
      margin-left: 16px;
    }

    .gift-card__button {
      border-radius: 32px;
      background: var(--neutral-50, #F5F5F5);
      color: var(--neutral-1000, #3C3833);
      text-align: center;
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 20px;
      font-weight: 600;
      line-height: 20px; /* 100% */
      text-transform: capitalize;
      padding: 12px 20px;
      border: 0;
      cursor: pointer;

      &.gift-card__button--active {
        background: #183D4F;
        color: var(--shades-white, #FFF);
      }
    }

    .gift-card__input {
      border-radius: 2px;
      background: var(--neutral-50, #F5F5F5);
      padding: 10px;
      color: var(--neutral-700, #767370);
      font-size: 16px;
      font-weight: 400;
      line-height: 28px; /* 175% */
      border: 0;
      outline: none;
      width: 100%;

      & + .gift-card__input {
        margin-top: 12px;
      }
    }

    .gift-card__form-section + .gift-card__form-section {
      margin-top: 32px;
    }

    .gift-card__denominations, .gift-card__methods {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }

    .gift-card__add-to-cart {
      border-radius: 32px;
      background: var(--primary-300, #E37F31);
      color: var(--shades-white, #FFF);
      text-align: center;
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 20px;
      font-weight: 800;
      line-height: 20px; /* 100% */
      text-transform: capitalize;
      border: 0;
      padding: 12px 20px;
    }
  }
}
</style>
